/**
 * Barras divergentes desde un cero central: el flujo por actividad.
 *
 * Aquí el color representa el SIGNO, no la actividad — entra o sale efectivo—,
 * así que no usa los tokens de serie: usarlos diría que operación, inversión y
 * financiamiento son tres entidades distintas, que es verdad, pero no es lo que
 * el lector pregunta de un flujo. Y como el color nunca puede ser el único
 * canal, la cifra va SIEMPRE con su paréntesis: un negativo se lee igual en una
 * fotocopia.
 */

import { formatMoney } from "../format";
import { type Cifra, cifra } from "./escala";

export interface Tramo {
  id: string;
  label: string;
  /** Crudo de la API: puede venir como cadena. Se convierte al entrar. */
  value: Cifra;
}

export function BarrasSigno({ tramos }: { tramos: Tramo[] }) {
  const limpios = tramos.map((t) => ({ ...t, value: cifra(t.value) }));
  const tope = Math.max(...limpios.map((t) => (t.value === null ? 0 : Math.abs(t.value))), 1);
  return (
    <div className="viz-signo">
      {limpios.map((t) => {
        // Un tramo SIN dato no se dibuja. Convertirlo en cero decía que por esa
        // actividad no se movió efectivo, y cero es una afirmación contable
        // distinta de «no lo sabemos»: `formatMoney(null)` ya escribe «—».
        const v = t.value;
        const hay = v !== null;
        const proporcion = hay ? (Math.abs(v) / tope) * 50 : 0;
        const sale = hay && v < 0;
        return (
          <div className="viz-signo-fila" key={t.id}>
            <span className="viz-signo-rotulo">{t.label}</span>
            <span className="viz-signo-riel">
              <span className="viz-signo-cero" aria-hidden="true" />
              {hay && (
                <span
                  className={`viz-signo-barra ${sale ? "sale" : "entra"}`}
                  style={
                    sale
                      ? { right: "50%", width: `${proporcion}%` }
                      : { left: "50%", width: `${proporcion}%` }
                  }
                />
              )}
            </span>
            <span className="viz-signo-cifra num">{formatMoney(v)}</span>
          </div>
        );
      })}
    </div>
  );
}
